import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { useGroup } from '../lib/GroupContext'
import { getMovieBackdrops } from '../lib/tmdb'
import { track, Events } from '../lib/analytics'

const SHARE_ORIGIN = typeof window !== 'undefined' ? window.location.origin : 'https://flickpick.com.au'
const SLUG_ALPHABET = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789'

// URL-safe random slug. We skip 0/O/1/l/i so humans don't misread shares.
function generateSlug(length = 10) {
  const cryptoRef = typeof crypto !== 'undefined' ? crypto : null
  const bytes = cryptoRef ? cryptoRef.getRandomValues(new Uint8Array(length)) : null
  let out = ''
  for (let i = 0; i < length; i++) {
    const n = bytes ? bytes[i] : Math.floor(Math.random() * 256)
    out += SLUG_ALPHABET[n % SLUG_ALPHABET.length]
  }
  return out
}

/**
 * Backdrop slideshow — crossfades between TMDB backdrop images.
 * Starts dark, fades in on reveal.
 */
function BackdropSlideshow({ images, revealed }) {
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    if (!revealed || images.length <= 1) return
    const interval = setInterval(() => {
      setCurrentIndex(i => (i + 1) % images.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [revealed, images.length])

  if (!images.length) return null

  return (
    <div className="fixed inset-0 z-0">
      {images.map((src, i) => (
        <div
          key={src}
          className="absolute inset-0"
          style={{
            opacity: revealed && i === currentIndex ? 1 : 0,
            transition: 'opacity 1.5s ease-in-out',
          }}
        >
          <img
            src={src}
            alt=""
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.3) saturate(1.2)' }}
          />
        </div>
      ))}
      {/* Gradient overlays for readability */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 20%, rgba(0,0,0,0.7) 100%)',
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 30%, transparent 60%, rgba(0,0,0,0.8) 100%)',
        }}
      />
    </div>
  )
}

export default function Result() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { activeGroup } = useGroup()
  const [session, setSession] = useState(null)
  const [movie, setMovie] = useState(null)
  const [suggester, setSuggester] = useState(null)
  const [saving, setSaving] = useState(false)
  const [revealed, setRevealed] = useState(false)
  const [backdrops, setBackdrops] = useState([])

  // Local persistence state — tracked so Share and "Let's Watch It" can each
  // lazily create the watch_history row without double-inserting.
  const [watchHistoryId, setWatchHistoryId] = useState(null)
  const [shareSlug, setShareSlug] = useState(null)
  const [sharing, setSharing] = useState(false)
  const [shareModal, setShareModal] = useState(null) // { url }
  const [toast, setToast] = useState('')

  // Solo mode is detected by the /solo/result route — the winning movie and
  // selected movies are passed through router state. No DB writes happen until
  // the user hits "Let's Watch It" or "Share this pick".
  const isSolo = location.pathname.startsWith('/solo')
  const soloSelectedMovies = location.state?.selectedMovies || []

  // Guard so the reveal-time analytics fire exactly once per result.
  const revealTrackedRef = useRef(false)

  useEffect(() => {
    loadResult()

    // Short pause builds tension, then reveal.
    const timer = setTimeout(() => setRevealed(true), 2500)
    return () => clearTimeout(timer)
  }, [user])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 2400)
    return () => clearTimeout(t)
  }, [toast])

  // Fire once when the result is actually shown (movie loaded + reveal timer
  // elapsed). For solo, this is the "end of flow" moment too.
  useEffect(() => {
    if (!revealed || !movie || revealTrackedRef.current) return
    revealTrackedRef.current = true
    const mode = isSolo ? 'solo' : 'group'
    track(Events.PickRevealed, { mode, tmdb_id: movie.tmdb_id })
    if (isSolo) {
      track(Events.SoloPickCompleted, { tmdb_id: movie.tmdb_id, picks: soloSelectedMovies.length })
    }
  }, [revealed, movie, isSolo, soloSelectedMovies.length])

  async function loadResult() {
    if (isSolo) {
      const soloMovie = location.state?.movie
      if (!soloMovie) { navigate('/spin-mode'); return }
      setMovie(soloMovie)
      if (soloMovie.tmdb_id) {
        getMovieBackdrops(soloMovie.tmdb_id)
          .then(imgs => setBackdrops(imgs))
          .catch(() => {})
      }
      return
    }

    if (!activeGroup) { navigate('/'); return }

    const { data: completedSession } = await supabase
      .from('movie_night_sessions')
      .select('*, movies(*)')
      .eq('group_id', activeGroup.id)
      .eq('status', 'complete')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (!completedSession || !completedSession.winning_movie_id) {
      navigate('/')
      return
    }
    setSession(completedSession)
    setMovie(completedSession.movies)

    // Fetch backdrop images from TMDB
    if (completedSession.movies?.tmdb_id) {
      getMovieBackdrops(completedSession.movies.tmdb_id)
        .then(imgs => setBackdrops(imgs))
        .catch(() => {})
    }

    // Find who submitted the winning movie
    const { data: sub } = await supabase
      .from('session_submissions')
      .select('*, profiles!session_submissions_user_id_fkey(display_name)')
      .eq('session_id', completedSession.id)
      .eq('movie_id', completedSession.winning_movie_id)
      .maybeSingle()

    if (sub?.profiles?.display_name) setSuggester(sub.profiles.display_name)
  }

  // Idempotent. Creates the watch_history row (and the solo session +
  // submissions for solo mode) the first time it's called, and returns the
  // existing watch_history id on subsequent calls. Used by both "Let's Watch
  // It" and "Share this pick".
  async function ensureWatchHistory() {
    if (watchHistoryId) return watchHistoryId

    if (isSolo) {
      const now = new Date().toISOString()
      const { data: soloSession, error: sessionErr } = await supabase
        .from('movie_night_sessions')
        .insert({
          mode: 'solo',
          user_id: user.id,
          created_by: user.id,
          status: 'complete',
          winning_movie_id: movie.id,
          spun_at: now,
          completed_at: now,
        })
        .select()
        .single()
      if (sessionErr) throw sessionErr

      if (soloSelectedMovies.length > 0) {
        const rows = soloSelectedMovies
          .filter(m => m?.id)
          .map(m => ({
            session_id: soloSession.id,
            user_id: user.id,
            movie_id: m.id,
          }))
        if (rows.length > 0) {
          const { error: subErr } = await supabase.from('session_submissions').insert(rows)
          if (subErr) throw subErr
        }
      }

      const { data: history, error: historyErr } = await supabase
        .from('watch_history')
        .insert({
          user_id: user.id,
          movie_id: movie.id,
          session_id: soloSession.id,
          suggested_by: user.id,
          watched_on: now.split('T')[0],
        })
        .select('id, share_slug')
        .single()
      if (historyErr) throw historyErr

      setWatchHistoryId(history.id)
      if (history.share_slug) setShareSlug(history.share_slug)
      return history.id
    }

    // Group mode
    if (!session) throw new Error('No active session')

    const { data: sub } = await supabase
      .from('session_submissions')
      .select('user_id')
      .eq('session_id', session.id)
      .eq('movie_id', movie.id)
      .maybeSingle()

    const { data: history, error: historyErr } = await supabase
      .from('watch_history')
      .insert({
        group_id: session.group_id,
        movie_id: movie.id,
        session_id: session.id,
        suggested_by: sub?.user_id || null,
        watched_on: new Date().toISOString().split('T')[0],
      })
      .select('id, share_slug')
      .single()
    if (historyErr) throw historyErr

    await supabase
      .from('movie_night_sessions')
      .update({ completed_at: new Date().toISOString() })
      .eq('id', session.id)

    setWatchHistoryId(history.id)
    if (history.share_slug) setShareSlug(history.share_slug)
    return history.id
  }

  // Ensures the current watch_history row has a share_slug, creating one on
  // the fly if needed. Returns the slug string. Retries once on conflict in
  // the (very unlikely) event of a collision.
  async function ensureShareSlug() {
    if (shareSlug) return shareSlug
    const id = await ensureWatchHistory()

    for (let attempt = 0; attempt < 2; attempt++) {
      const candidate = generateSlug(10)
      const { data, error } = await supabase
        .from('watch_history')
        .update({ share_slug: candidate })
        .eq('id', id)
        .is('share_slug', null)
        .select('share_slug')
        .maybeSingle()

      if (!error && data?.share_slug) {
        setShareSlug(data.share_slug)
        return data.share_slug
      }

      // Either someone else already set it (update returned null) — re-read;
      // or slug collision (rare) — loop once.
      if (!error) {
        const { data: existing } = await supabase
          .from('watch_history')
          .select('share_slug')
          .eq('id', id)
          .maybeSingle()
        if (existing?.share_slug) {
          setShareSlug(existing.share_slug)
          return existing.share_slug
        }
      }
    }
    throw new Error('Could not generate share slug')
  }

  async function addToHistory() {
    if (!movie || saving) return
    setSaving(true)
    try {
      await ensureWatchHistory()
      track(Events.MovieWatched, { mode: isSolo ? 'solo' : 'group' })
      navigate('/history')
    } catch (err) {
      console.error(err)
      alert('Failed to save. Try again.')
      setSaving(false)
    }
  }

  async function handleShare() {
    if (!movie || sharing) return
    setSharing(true)
    try {
      const slug = await ensureShareSlug()
      const url = `${SHARE_ORIGIN}/result/${slug}`
      const shareData = {
        title: `FlickPick chose ${movie.title}`,
        text: 'Our movie night verdict. Someone had to decide.',
        url,
      }
      const mode = isSolo ? 'solo' : 'group'

      if (navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
        try {
          await navigator.share(shareData)
          track(Events.ResultShared, { mode, share_method: 'native' })
          setToast('Shared!')
        } catch (err) {
          if (err?.name !== 'AbortError') {
            // Share was attempted but failed — fall back to modal
            setShareModal({ url })
          }
        }
      } else {
        setShareModal({ url })
      }
    } catch (err) {
      console.error(err)
      alert('Could not create share link. Try again.')
    } finally {
      setSharing(false)
    }
  }

  async function handleCopy(url) {
    try {
      await navigator.clipboard.writeText(url)
      track(Events.ResultShared, {
        mode: isSolo ? 'solo' : 'group',
        share_method: 'copy',
      })
      setToast('Link copied!')
      setShareModal(null)
    } catch {
      setToast('Copy failed — select the text and copy manually.')
    }
  }

  if (!movie) return null
  if (!isSolo && !session) return null

  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center px-6 relative overflow-hidden">
      <BackdropSlideshow images={backdrops} revealed={revealed} />

      <div className="text-center z-10" style={{
        opacity: revealed ? 1 : 0,
        transform: revealed ? 'scale(1) translateY(0)' : 'scale(0.8) translateY(30px)',
        transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
        maxWidth: '92vw',
      }}>
        <div className="text-[13px] fp-gold-text uppercase tracking-[0.3em] font-semibold mb-1"
             style={{ textShadow: '0 0 24px rgba(212, 175, 55, 0.6)' }}>
          Tonight's Pick
        </div>
        <div className="text-4xl mb-3">🍿</div>

        <div className="rounded-2xl overflow-hidden mx-auto my-4 bg-[var(--fp-border)] relative"
             style={{
               boxShadow: '0 0 80px var(--fp-red-glow), 0 0 140px rgba(212, 175, 55, 0.25), 0 12px 40px rgba(0,0,0,0.7)',
               border: '3px solid var(--fp-gold)',
               width: 'min(260px, 60vw)',
               aspectRatio: '2/3',
             }}>
          {movie.poster_url ? (
            <img src={movie.poster_url} alt={movie.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-6xl">🎬</div>
          )}
        </div>

        <h1 className="fp-display text-4xl text-white mt-4"
            style={{ textShadow: '0 2px 24px rgba(0,0,0,0.7), 0 0 40px var(--fp-red-glow)', letterSpacing: '-0.03em' }}>
          {movie.title}
        </h1>
        <p className="text-sm text-[var(--fp-text-muted)] mt-2">
          {[movie.year, movie.genre, movie.runtime_minutes && `${Math.floor(movie.runtime_minutes / 60)}h ${movie.runtime_minutes % 60}m`, movie.tmdb_rating && `★ ${movie.tmdb_rating}`].filter(Boolean).join(' · ')}
        </p>
        {/* Suggester credit is intentionally suppressed in solo mode —
            all picks are the user's own. */}
        {!isSolo && suggester && (
          <p className="text-[13px] mt-1.5" style={{ color: 'var(--fp-red)', opacity: 0.8 }}>
            Suggested by {suggester}
          </p>
        )}

        <div className="mt-8 max-w-xs mx-auto">
          <button
            onClick={addToHistory}
            disabled={saving}
            className="fp-btn fp-btn-gold w-full"
            style={{ boxShadow: '0 8px 32px rgba(212, 175, 55, 0.4)' }}
          >
            {saving ? 'Saving…' : "Let's Watch It"}
          </button>

          <button
            onClick={handleShare}
            disabled={sharing}
            className="mt-3 w-full fp-btn border-none cursor-pointer flex items-center justify-center gap-2"
            style={{
              background: 'transparent',
              color: 'var(--fp-gold)',
              border: '1.5px solid var(--fp-gold)',
              minHeight: '44px',
              opacity: sharing ? 0.6 : 1,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
              <polyline points="16 6 12 2 8 6"/>
              <line x1="12" y1="2" x2="12" y2="15"/>
            </svg>
            {sharing ? 'Preparing…' : 'Share this pick'}
          </button>

          {isSolo && (
            <button
              onClick={() => navigate('/solo')}
              disabled={saving}
              className="mt-3 text-sm font-semibold text-[var(--fp-text-muted)] bg-transparent border-none cursor-pointer hover:text-white transition-colors"
            >
              Spin again
            </button>
          )}
          <p className="text-[11px] text-[var(--fp-text-dim)] mt-3">
            No take-backs. The wheel has spoken.
          </p>
        </div>
      </div>

      {shareModal && (
        <CopyLinkModal
          url={shareModal.url}
          onClose={() => setShareModal(null)}
          onCopy={() => handleCopy(shareModal.url)}
        />
      )}

      {toast && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-full text-sm font-semibold text-white shadow-lg"
          style={{
            background: 'var(--fp-gold)',
            color: '#0a0a0a',
            boxShadow: '0 8px 24px rgba(212, 175, 55, 0.45)',
            bottom: 'calc(env(safe-area-inset-bottom, 16px) + 40px)',
            maxWidth: '90vw',
          }}
        >
          {toast}
        </div>
      )}
    </div>
  )
}

function CopyLinkModal({ url, onClose, onCopy }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-5"
      style={{ background: 'rgba(0,0,0,0.85)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm bg-[var(--fp-card)] border border-[var(--fp-border)] rounded-2xl p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="fp-display text-xl text-white">Share this pick</h2>
          <button
            onClick={onClose}
            className="text-sm text-[var(--fp-text-muted)] bg-transparent border-none cursor-pointer hover:text-white"
          >
            Done
          </button>
        </div>

        <p className="text-xs text-[var(--fp-text-muted)] mb-3">
          Copy the link and paste it anywhere.
        </p>

        <div
          className="fp-input flex items-center gap-3"
          style={{ padding: '12px 14px' }}
        >
          <span className="text-sm text-white truncate flex-1 font-mono">{url}</span>
        </div>

        <button
          onClick={onCopy}
          className="fp-btn fp-btn-primary w-full mt-5"
          style={{ minHeight: '44px' }}
        >
          Copy link
        </button>
      </div>
    </div>
  )
}
